import React, { useState, useEffect, useRef } from 'react';
import { Twitter, ArrowRight, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Button } from '../ui/Button';
import { Link } from 'react-router-dom';
import SectionWrapper from './SectionWrapper';

const screenshots = [
  {
    id: 0,
    image: "/images/optimized/screenshots/darkmode.jpg",
    title: "Dark Mode Available",
    description: "By popular demand, dark mode has been added !",
    alt: "Dark mode interface showing the trading journal in dark theme"
  },
  {
    id: 1,
    image: "/images/optimized/screenshots/calendar.jpg",
    title: "Calendar View",
    description: "Build positive momentum by focusing on following your trading plan.",
    alt: "Calendar view showing daily trading activity"
  },
  {
    id: 2,
    image: "/images/optimized/screenshots/calendar2.jpg",
    title: "Trade Gallery",
    description: "Scroll through all of your trade screenshots. Excellent for visualizing your trading.",
    alt: "Gallery view showing screenshots of individual trades"
  },
  {
    id: 3,
    image: "/images/optimized/screenshots/tradingmodel.jpg",
    title: "Trading Models",
    description: "Create and track individual trading strategies.",
    alt: "Trading model interface showing a trading strategy"
  },
  {
    id: 4,
    image: "/images/optimized/screenshots/allmodels.jpg",
    title: "All Trading Models",
    description: "Compare multiple strategies side by side.",
    alt: "Overview of all trading models and their performance"
  },
  {
    id: 5,
    image: "/images/optimized/screenshots/tradelog.jpg",
    title: "Trade Log",
    description: "Record every aspect of your trades, including plan adherence.",
    alt: "Trade log interface showing recorded trades"
  },
  {
    id: 6,
    image: "/images/optimized/screenshots/addtrade.jpg",
    title: "Trading View Integration",
    description: "Easily log new trades with our comprehensive form or Trading View copy/paste.",
    alt: "Interface for adding new trades to the journal"
  },
  {
    id: 7,
    image: "/images/optimized/screenshots/notes.jpg",
    title: "Notes",
    description: "Integrated mood board notes system to organize your thoughts.",
    alt: "Notes interface showing trading journal entries"
  },
  {
    id: 8,
    image: "/images/optimized/screenshots/dashboard1.jpg",
    title: "Performance Dashboard",
    description: "Get a complete overview of your trading performance.",
    alt: "Trading dashboard showing performance metrics"
  },
  {
    id: 9,
    image: "/images/optimized/screenshots/dashboard2.jpg",
    title: "Advanced Analytics",
    description: "Dive deeper into patterns with advanced analytics.",
    alt: "Detailed trading analytics and statistics"
  },
  {
    id: 10,
    image: "/images/optimized/screenshots/aianalysis.jpg",
    title: "AI Analysis",
    description: "Receive detailed AI-powered analysis of your trades.",
    alt: "AI-generated trading analysis report"
  },
  {
    id: 11,
    image: "/images/optimized/screenshots/ai.jpg",
    title: "AI Trading Coach",
    description: "Get personalized insights and recommendations from our AI coach.",
    alt: "AI Trading Coach interface showing personalized trading analysis"
  }
];

const ScreenshotShowcase: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const loadedImagesRef = useRef<Set<number>>(new Set());

  // Preload current and adjacent images
  useEffect(() => {
    const imagesToLoad = [
      currentSlide,
      (currentSlide + 1) % screenshots.length,
      currentSlide === 0 ? screenshots.length - 1 : currentSlide - 1
    ];
    
    const imageObjects: HTMLImageElement[] = [];
    
    imagesToLoad.forEach(idx => {
      if (!loadedImagesRef.current.has(idx)) {
        const img = new Image();
        imageObjects.push(img);
        img.src = screenshots[idx].image;
        img.onload = () => {
          loadedImagesRef.current.add(idx);
          setLoadedImages(new Set(loadedImagesRef.current));
        };
      }
    });
    
    // Cleanup function to prevent memory leaks
    return () => {
      imageObjects.forEach(img => {
        img.onload = null;
      });
    };
  }, [currentSlide]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const prevSlide = () => goToSlide(currentSlide === 0 ? screenshots.length - 1 : currentSlide - 1);
  const nextSlide = () => goToSlide(currentSlide === screenshots.length - 1 ? 0 : currentSlide + 1);

  const handleImageLoad = () => setIsLoading(false);

  return (
    <SectionWrapper>
      <div id="screenshot-showcase">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-gray-950">
            See What&apos;s <span className="gradient-text">Inside</span>
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl shadow-xl">
            <div className="p-6 border-b border-gray-300/50">
              <h3 className="font-display text-2xl font-semibold text-gray-900">
                Trading Journal Features
              </h3>
            </div>
            <div className="relative" style={{ aspectRatio: '16/9' }}>
              {screenshots.map((screenshot, idx) => (
                <div
                  key={screenshot.id}
                  className={`absolute inset-0 transition-opacity duration-300 ${
                    idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  {(loadedImages.has(idx) || idx === currentSlide) && (
                    <img
                      src={screenshot.image}
                      alt={screenshot.alt}
                      className="w-full h-full object-contain"
                      onLoad={idx === currentSlide ? handleImageLoad : undefined}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                  )}
                </div>
              ))}
              
              <button
                type="button"
                onClick={prevSlide}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 rounded-full bg-white/80 shadow-md flex items-center justify-center z-20"
                aria-label="Previous slide"
              >
                <ChevronLeft size={30} className="text-gray-800" />
              </button>
              <button
                type="button"
                onClick={nextSlide}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 rounded-full bg-white/80 shadow-md flex items-center justify-center z-20"
                aria-label="Next slide"
              >
                <ChevronRight size={30} className="text-gray-800" />
              </button>
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3 pb-4 z-20">
                {screenshots.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToSlide(idx)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      idx === currentSlide ? 'bg-gray-800 scale-125' : 'bg-gray-400 hover:bg-gray-600'
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-300/50 bg-gray-50">
              <h4 className="text-2xl font-bold text-gray-900">
                {screenshots[currentSlide].title}
              </h4>
              <p className="text-gray-700">{screenshots[currentSlide].description}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-12">
          <Button
            as={Link}
            to="/trial"
            className="px-12 py-6 text-2xl font-semibold min-w-[280px] shadow-lg hover:shadow-xl bg-black hover:bg-gray-900 text-white !rounded-full transition-all duration-300"
          >
            Free Trial
            <ArrowRight size={28} className="ml-4" />
          </Button>
        </div>
      </div>
      </div>
    </SectionWrapper>
  );
};

export default ScreenshotShowcase;